// src/components/math/MathUtils.jsx

export class MathUtils {
  static mathJaxLoaded = false;
  static mathJaxPromise = null;

  static initializeMathJax() {
    if (this.mathJaxLoaded) return Promise.resolve();
    if (this.mathJaxPromise) return this.mathJaxPromise;

    this.mathJaxPromise = new Promise((resolve, reject) => {
      if (window.MathJax?.typesetPromise) {
        this.mathJaxLoaded = true;
        resolve();
        return;
      }

      window.MathJax = {
        tex: {
          inlineMath: [['\\(', '\\)'], ['$', '$']],
          displayMath: [['\\[', '\\]'], ['$$', '$$']],
          processEscapes: true,
          processEnvironments: true,
          macros: {
            R: '\\mathbb{R}',
            N: '\\mathbb{N}',
            Z: '\\mathbb{Z}',
            Q: '\\mathbb{Q}',
            C: '\\mathbb{C}',
            reals: '\\mathbb{R}',
            naturals: '\\mathbb{N}',
            Integers: '\\mathbb{Z}',
            infin: '\\infty',
            infinity: '\\infty',
            d: '\\mathrm{d}',
            e: '\\mathrm{e}',
            i: '\\mathrm{i}',
            implies: '\\Rightarrow',
            iff: '\\Leftrightarrow',
            grad: '\\nabla',
            div: '\\nabla\\cdot',
            curl: '\\nabla\\times',
            emptyset: '\\varnothing',
            forall: '\\forall',
            exists: '\\exists'
          }
        },
        chtml: {
          scale: 1,
          minScale: 0.5,
          mtextInheritFont: true,
          merrorInheritFont: true,
          adaptiveCSS: true
        },
        options: {
          enableMenu: false,
          skipHtmlTags: ['script', 'noscript', 'style', 'textarea', 'pre', 'code'],
          ignoreHtmlClass: 'tex2jax_ignore',
          processHtmlClass: 'tex2jax_process'
        },
        startup: {
          typeset: false
        }
      };

      const script = document.createElement('script');
      // Use CHTML instead of SVG - avoids NaN dimension issues
      script.src = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-chtml.js';
      script.async = true;

      script.onload = () => {
        window.MathJax.startup.promise.then(() => {
          this.mathJaxLoaded = true;
          resolve();
        });
      };

      script.onerror = reject;
      document.head.appendChild(script);
    });

    return this.mathJaxPromise;
  }

  static normalizeAliases(text) {
    if (!text) return text;
    return text
      .replace(/\\Reals\b/g, '\\mathbb{R}')
      .replace(/\\Naturals\b/g, '\\mathbb{N}')
      .replace(/\\Integers\b/g, '\\mathbb{Z}')
      .replace(/\\infin\b/g, '\\infty')
      .replace(/\\infinity\b/g, '\\infty')
      // Arrow shorthands
      .replace(/(?<!\\)(->|-->)/g, '\\to ')
      .replace(/(?<!\\)=>+/g, '\\implies ')
      .replace(/(?<!\\)<->/g, '\\leftrightarrow ')
      .replace(/(?<!\\)<=>/g, '\\iff ');
  }

  static async renderMath(element) {
    if (!element) return;

    // Skip if no text content
    const textContent = element.textContent?.trim();
    if (!textContent) return;

    await this.initializeMathJax();

    if (window.MathJax?.typesetPromise) {
      try {
        await window.MathJax.typesetPromise([element]);
      } catch (err) {
        console.warn('MathJax rendering warning:', err.message);
      }
    }
  }

  static processMathExpressions(text) {
    if (!text) return text;

    let result = text;

    // 1. Normalize delimiters to standard $ and $$
    result = result.replace(/\\\[([\s\S]*?)\\\]/g, (_, m) => {
      const trimmed = m.trim();
      return trimmed ? `$$${trimmed}$$` : '';
    });

    result = result.replace(/\\\(([\s\S]*?)\\\)/g, (_, m) => {
      const trimmed = m.trim();
      return trimmed ? `$${trimmed}$` : '';
    });

    // 2. Normalize aliases (including shorthands like ->)
    result = this.normalizeAliases(result);

    // 3. Temporarily protect existing math blocks to avoid double-wrapping
    const mathBlocks = [];
    result = result.replace(/(\$\$[\s\S]*?\$\$|\$[^\$]+\$)/g, (match) => {
      mathBlocks.push(match);
      return `___MATH_BLOCK_${mathBlocks.length - 1}___`;
    });

    
    // 4. Wrap standalone LaTeX arrow commands in math delimiters
    result = result.replace(/\\(to|rightarrow|Rightarrow|leftarrow|Leftarrow|longrightarrow|Longrightarrow|longleftarrow|Longleftarrow|leftrightarrow|Leftrightarrow|longleftrightarrow|mapsto|implies|impliedby|iff)\b/g, '$\\$1$');

    // 5. Restore protected math blocks
    result = result.replace(/___MATH_BLOCK_(\d+)___/g, (_, index) => {
      return mathBlocks[parseInt(index)];
    });

    // Remove empty math blocks
    result = result.replace(/\$\$\s*\$\$/g, '');
    result = result.replace(/\$\s*\$/g, '');

    return result;
  }

  static containsMathExpressions(text) {
    if (!text) return false;

    return [
      /\$\$[\s\S]+?\$\$/,
      /\$[^$]+\$/,
      /->|=>|<->|<=>/,
      /\\(to|rightarrow|leftarrow|leftrightarrow|Rightarrow|Leftarrow|Leftrightarrow|mapsto|hookrightarrow|hookleftarrow|uparrow|downarrow|updownarrow|nearrow|searrow|swarrow|nwarrow|Longrightarrow|Longleftarrow|Longleftrightarrow|longrightarrow|longleftarrow|longleftrightarrow|gets|rightarrowtail|leftarrowtail|twoheadrightarrow|twoheadleftarrow|rightsquigarrow|leftrightsquigarrow|dashrightarrow|dashleftarrow|xrightarrow|xleftarrow|xleftrightarrow|xRightarrow|xLeftarrow|xLeftrightarrow|implies|impliedby|iff)\b/,
      /\\mathbb/,
      /\\infty/,
      /\\Reals/,
      /\\Naturals/,
      /\\Integers/,
      /\\frac/,
      /\\sqrt/,
      /\\sum/,
      /\\int/,
      /\\lim/,
      /\\sin|\\cos|\\tan/,
      /\\leq|\\geq|\\neq/,
      /\\in\b|\\notin\b/,
      /\\cup|\\cap/,
      /\\forall|\\exists/
    ].some(rx => rx.test(text));
  }

  static protectMathExpressions(text) {
    let result = text;

    result = result.replace(/\$\$([\s\S]*?)\$\$/g, m =>
      `<DISPLAYMATH>${encodeURIComponent(m)}</DISPLAYMATH>`
    );

    result = result.replace(/(?<!\$)\$(?!\$)([^$]+)\$(?!\$)/g, m =>
      `<INLINEMATH>${encodeURIComponent(m)}</INLINEMATH>`
    );

    return result;
  }

  static restoreMathExpressions(text) {
    return text
      .replace(/<DISPLAYMATH>(.*?)<\/DISPLAYMATH>/g, (_, m) =>
        decodeURIComponent(m)
      )
      .replace(/<INLINEMATH>(.*?)<\/INLINEMATH>/g, (_, m) =>
        decodeURIComponent(m)
      );
  }

  static async loadMarked() {
    if (window.marked) return;

    await new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/marked@9.1.6/marked.min.js';
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });

    if (window.marked?.setOptions) {
      window.marked.setOptions({
        gfm: true,
        breaks: true
      });
    }
  }

  static processMarkdown(text) {
    if (!window.marked) return text.replace(/\n/g, '<br>');
    return window.marked.parse(text);
  }

  static async processContentWithMarkdown(text) {
    if (!text) return '';

    await this.loadMarked();

    const normalised = this.processMathExpressions(text);
    const protectedText = this.protectMathExpressions(normalised);
    const html = this.processMarkdown(protectedText);

    return this.restoreMathExpressions(html);
  }
}
